import Highcharts from "highcharts";

// ─── Types ────────────────────────────────────────────────────────────────────

type SingleBarRow = {
  name: string;
  value: number;
  percentage?: number;
  // Carried over when this row came from a converted "bar" chart whose rows
  // had their own backend color (see BarRenderer.ts/BarChartRow) — takes
  // priority over CATEGORY_COLORS/FALLBACK_COLORS below so a bar→single_bar
  // conversion keeps each segment's original color instead of reassigning
  // one from the fixed category map.
  color?: string;
};

// ─── Colors ───────────────────────────────────────────────────────────────────

// Fixed, exact-match category colors (not substring/regex — "Not treated"
// contains "treated", so a substring match against "treated" would
// misfire on it). Matched case/whitespace-insensitively against the
// backend's `name` field.
const CATEGORY_COLORS: Record<string, string> = {
  treated: "#1E3A8A",
  transferred: "#3B82F6",
  "not treated": "#FDBA74",
  "prescriber resolution unknown": "#9CA3AF",
};

// Stack order bottom-to-top. Backend doesn't guarantee row order, so rows are
// re-sorted into this fixed order before charting; anything unrecognized
// (not in CATEGORY_COLORS) is pushed to the end and gets a fallback color.
const CATEGORY_ORDER = Object.keys(CATEGORY_COLORS);
const FALLBACK_COLORS = ["#2563EB", "#D97706", "#7C3AED", "#0D9488"];

const normalizeName = (name: string) => name.trim().toLowerCase();

type Segment = { name: string; value: number; pct: number; color: string };

const toSegments = (rows: SingleBarRow[]): Segment[] => {
  const sorted = [...rows].sort((a, b) => {
    const ai = CATEGORY_ORDER.indexOf(normalizeName(a.name));
    const bi = CATEGORY_ORDER.indexOf(normalizeName(b.name));
    return (
      (ai === -1 ? CATEGORY_ORDER.length : ai) -
      (bi === -1 ? CATEGORY_ORDER.length : bi)
    );
  });

  const total = sorted.reduce((sum, r) => sum + (Number(r.value) || 0), 0);

  return sorted.map((r, i) => {
    const value = Number(r.value) || 0;
    const pct = r.percentage ?? (total > 0 ? (value / total) * 100 : 0);
    return {
      name: r.name,
      value,
      pct,
      color:
        r.color ??
        CATEGORY_COLORS[normalizeName(r.name)] ??
        FALLBACK_COLORS[i % FALLBACK_COLORS.length],
    };
  });
};

// ─── Renderer ─────────────────────────────────────────────────────────────────

// chart_type: "single_bar" — single-category 100%-stacked column, one series
// per row so the legend can show each category's own share. Registered
// through the shared chart registry (not a bespoke React widget) so it gets
// the same hamburger context menu, export options, and "Convert to bar
// chart" behavior every other registry chart type already has.
export const renderSingleBar = (
  data: unknown,
  _spec: unknown,
  title: string,
): Highcharts.Options => {
  const rows = Array.isArray(data) ? (data as SingleBarRow[]) : [];
  const segments = toSegments(rows);

  return {
    chart: {
      type: "column",
      spacing: [10, 10, 10, 10],
      style: { fontFamily: "inherit" },
    },
    title: { text: "" },
    credits: { enabled: false },
    // Same contextButton offset every other chart config in this codebase
    // uses (barChartConfig, pieChartConfig, ...) — tucks Highcharts' own
    // native exporting button up behind the widget card's custom hamburger
    // icon, which triggers it synthetically (WidgetRenderer.tsx). Omitting
    // this left Highcharts' default (visible, inside the plot area) button
    // position, showing a second, real hamburger button on the chart.
    exporting: {
      enabled: true,
      fallbackToExportServer: true,
      buttons: {
        contextButton: {
          align: "right",
          verticalAlign: "top",
          y: -43,
          x: -30,
        },
      },
    },
    xAxis: {
      categories: [title],
      lineColor: "#E2E8F0",
      labels: { style: { fontSize: "11px", color: "#334155" } },
    },
    yAxis: {
      min: 0,
      max: 100,
      tickInterval: 10,
      // Column stacking defaults to putting the FIRST series on top of the
      // stack (Highcharts' reversedStacks defaults to true for columns) —
      // reversed here so series order reads bottom-to-top.
      reversedStacks: false,
      gridLineColor: "#f0f0f0",
      title: { text: undefined },
      labels: {
        style: { fontSize: "11px", color: "#6c757d" },
        formatter() {
          return `${this.value}%`;
        },
      },
    },
    legend: {
      enabled: true,
      itemStyle: { fontSize: "12px", fontWeight: "normal" },
      // Default legend only shows the series name — append each category's
      // share so the legend still reads "Treated 9.68%" like before.
      labelFormatter(this: Highcharts.Point | Highcharts.Series) {
        const y = "yData" in this ? this.yData?.[0] : undefined;
        return typeof y === "number" ? `${this.name} ${y.toFixed(2)}%` : this.name;
      },
    },
    tooltip: {
      useHTML: true,
      formatter(this: Highcharts.Point) {
        const seg = segments.find((s) => s.name === this.series.name);
        return `<div style="padding:2px 4px;">
          <div style="font-size:12px;font-weight:700;margin-bottom:2px;">${this.series.name}</div>
          <div style="font-size:11px;">Count: <b>${(seg?.value ?? 0).toLocaleString()}</b></div>
          <div style="font-size:11px;">Share: <b>${this.y}%</b></div>
        </div>`;
      },
    },
    plotOptions: {
      column: {
        stacking: "normal",
        borderWidth: 0,
        dataLabels: { enabled: false },
        // Cap the bar's width so a single category doesn't stretch to fill
        // the whole plot area — shrinks further on narrow widgets, never
        // grows past this on wide ones.
        maxPointWidth: 80,
      },
    },
    series: segments.map((s) => ({
      type: "column" as const,
      name: s.name,
      data: [Math.round(s.pct * 100) / 100],
      color: s.color,
    })),
  };
};
